import React from 'react';
import { AuthProvider, useAuth } from 'react-oidc-context';
import { config, FAST_API_BASE_PEOPLE } from '../config';

const AppAuthContext = React.createContext(null);

const oidcConfig = {
  authority: config.auth.authority,
  client_id: config.auth.clientId,
  redirect_uri: config.auth.redirectUri,
  response_type: config.auth.responseType,
  scope: config.auth.scope
};

const getCognitoGroups = (user) =>
  Array.isArray(user?.profile?.['cognito:groups']) ? user.profile['cognito:groups'] : [];

function useCleanAuthCallbackUrl(isLoading, isAuthenticated) {
  React.useEffect(() => {
    if (isLoading || !isAuthenticated) {
      return;
    }

    const { search, hash, pathname } = window.location;
    if (!search) {
      return;
    }

    const params = new URLSearchParams(search);
    if (!params.has('code')) {
      return;
    }

    params.delete('code');
    params.delete('state');

    const remainingQuery = params.toString();
    const nextUrl = `${pathname}${remainingQuery ? `?${remainingQuery}` : ''}${hash || ''}`;
    window.history.replaceState({}, document.title, nextUrl);
  }, [isLoading, isAuthenticated]);
}

function usePersistPersonId(isAuthenticated, accessToken) {
  const [selfFetchError, setSelfFetchError] = React.useState(null);

  React.useEffect(() => {
    setSelfFetchError(null);

    if (!(isAuthenticated && accessToken)) {
      return;
    }

    const controller = new AbortController();

    const fetchCurrentPerson = async () => {
      try {
        const response = await fetch(`${FAST_API_BASE_PEOPLE}/self`, {
          headers: {
            Authorization: `Bearer ${accessToken}`
          },
          signal: controller.signal
        });

        if (!response.ok) {
          throw new Error('Unable to load your HoggedIn profile. Some features may be unavailable.');
        }

        const data = await response.json();
        if (typeof data?.personid !== 'undefined') {
          sessionStorage.setItem('personid', String(data.personid));
          setSelfFetchError(null);
        } else {
          sessionStorage.removeItem('personid');
          if (process.env.NODE_ENV !== 'production') {
            // Log only the shape, never the payload: it contains profile data.
            console.error('Self endpoint response did not include a personid; keys:', Object.keys(data || {}));
          }
          setSelfFetchError('Unable to load your HoggedIn profile. Some features may be unavailable.');
        }
      } catch (err) {
        if (err?.name === 'AbortError') {
          return;
        }

        sessionStorage.removeItem('personid');
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error fetching current person via FastAPI:', err);
        }
        setSelfFetchError(err?.message || 'Unable to load your HoggedIn profile. Some features may be unavailable.');
      }
    };

    fetchCurrentPerson();

    return () => {
      controller.abort();
    };
  }, [isAuthenticated, accessToken]);

  return selfFetchError;
}

function AppAuthStateProvider({ children }) {
  const auth = useAuth();
  const accessToken = auth?.user?.access_token ?? null;
  const groups = React.useMemo(() => getCognitoGroups(auth.user), [auth.user]);
  const isAdmin = groups.includes('admin');
  const selfFetchError = usePersistPersonId(auth.isAuthenticated, accessToken);

  useCleanAuthCallbackUrl(auth.isLoading, auth.isAuthenticated);

  const signIn = React.useCallback(() => {
    auth.signinRedirect();
  }, [auth]);

  const signOut = React.useCallback(() => {
    auth.removeUser();
    window.location.href = `${config.auth.logoutDomain}/logout?client_id=${config.auth.clientId}&logout_uri=${encodeURIComponent(config.auth.logoutUri)}`;
  }, [auth]);

  const value = React.useMemo(
    () => ({
      auth,
      user: auth.user ?? null,
      profile: auth.user?.profile ?? null,
      accessToken,
      groups,
      isAdmin,
      isLoading: auth.isLoading,
      isAuthenticated: auth.isAuthenticated,
      error: auth.error,
      selfFetchError,
      signIn,
      signOut
    }),
    [
      auth,
      accessToken,
      groups,
      isAdmin,
      selfFetchError,
      signIn,
      signOut
    ]
  );

  return <AppAuthContext.Provider value={value}>{children}</AppAuthContext.Provider>;
}

export function AppAuthProvider({ children }) {
  return (
    <AuthProvider {...oidcConfig}>
      <AppAuthStateProvider>{children}</AppAuthStateProvider>
    </AuthProvider>
  );
}

export function useAppAuth() {
  const value = React.useContext(AppAuthContext);
  if (!value) {
    throw new Error('useAppAuth must be used within AppAuthProvider');
  }

  return value;
}
